/**
 * @fileoverview Role titles by appointment type on the Practitioner preset
 * (MOR-2354). For every appointment type the firm sees the SYSTEM DEFAULT the
 * job would derive (Liquidator, Voluntary Administrator, Bankruptcy Trustee…),
 * may type an OVERRIDE for that type alone, sees the EFFECTIVE title, and can
 * reset a type back to its default. A bankruptcy "Trustee" never bleeds into
 * a liquidation, administration or receivership.
 *
 * Legacy UNSCOPED values (`signatory_title` / `practitioner_title` stored by
 * the previous, type-blind setting) are never applied; they are shown here
 * with explicit actions — use for a chosen type, or discard — never guessed.
 */

import { useState } from "react";
import { ChevronDown, RotateCcw, Users } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { cn } from "@/utils/misc";
import { APPOINTMENT_TYPE_LABELS } from "@/components/pre-appointment/constants";
import {
  legacyUnscopedTitles,
  scopedTitleKey,
  TITLE_APPOINTMENT_TYPES,
  TITLE_KEYS,
  type TitleKey,
} from "~/convex/lib/titleScopes";
import {
  effectiveTitle,
  systemTitleDefault,
  TITLE_LABELS,
  withTypeReset,
} from "./-ui.title-by-appointment-type.helpers";

/** What "Apply to all presets" sends for one appointment type. */
export interface TitleRowPatch {
  appointmentType: string;
  /** Non-empty overrides on this row, keyed by scoped key. */
  set: Record<string, string>;
  /** Scoped keys this row leaves blank (reset on every preset). */
  clear: string[];
}

export function TitleByAppointmentType({
  values,
  onChange,
  onCommit,
  readOnly = false,
  defaultOpen = false,
  onApplyToAll,
  applyToAllLabel = "Apply to all presets",
}: {
  values: Readonly<Record<string, string>>;
  onChange?: (next: Record<string, string>) => void;
  /**
   * Fired after an input commits (blur). Used by the preset card to persist
   * the latest titles without a separate Save button.
   */
  onCommit?: () => void;
  readOnly?: boolean;
  /** Start with the table visible. Edit forms use this; the summary stays closed. */
  defaultOpen?: boolean;
  /**
   * Bulk action (MOR-2354): write this row's titles (and blanks as resets)
   * to every preset of the category at once, so a firm-wide house style is
   * not re-typed appointee by appointee. Omit to hide the action.
   */
  onApplyToAll?: (patch: TitleRowPatch) => void | Promise<void>;
  applyToAllLabel?: string;
}) {
  const legacy = legacyUnscopedTitles(values);
  const legacyEntries = Object.entries(legacy) as Array<[TitleKey, string]>;
  const set = (key: string, value: string) =>
    onChange?.({ ...values, [key]: value });
  // Collapsed by default — the table is tall and most presets never need it.
  // A leftover unscoped title is a warning the firm must act on, so that
  // case starts open rather than hiding the notice behind the toggle.
  const [open, setOpen] = useState(defaultOpen || legacyEntries.length > 0);
  const overrideCount = TITLE_APPOINTMENT_TYPES.filter((type) =>
    TITLE_KEYS.some(
      (titleKey) => effectiveTitle(values, titleKey, type).overridden,
    ),
  ).length;

  return (
    <div className="space-y-3">
      <TooltipProvider delayDuration={200}>
        <Tooltip open={open ? false : undefined}>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((current) => !current)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary/70 hover:text-primary"
            >
              <ChevronDown
                className={cn("size-3.5 transition", open && "rotate-180")}
              />
              {open
                ? "Hide practitioner and signatory titles"
                : "Set practitioner and signatory titles"}
              {overrideCount > 0 && (
                <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
                  {overrideCount} override{overrideCount === 1 ? "" : "s"}
                </span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px] text-xs">
            Default titles based on job type will apply unless overridden.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {open && (
        <p className="text-xs text-primary/40">
          Titles are derived from the appointment type. You can customise per
          appointment type. Changes will only apply to the relative appointment.
        </p>
      )}

      {open && (
        <>
          {legacyEntries.length > 0 && (
            <div
              role="note"
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
            >
              <p className="font-medium">
                Legacy title value not applied to any appointment type
              </p>
              {legacyEntries.map(([titleKey, value]) => (
                <div
                  key={titleKey}
                  className="mt-2 flex flex-wrap items-center gap-2"
                >
                  <span>
                    {TITLE_LABELS[titleKey]}: &ldquo;{value}&rdquo;
                  </span>
                  {!readOnly && (
                    <>
                      <select
                        aria-label={`Apply legacy ${TITLE_LABELS[titleKey].toLowerCase()} to an appointment type`}
                        className="h-7 rounded border border-amber-300 bg-background px-1 text-xs"
                        defaultValue=""
                        onChange={(e) => {
                          const type = e.target.value;
                          if (!type) return;
                          onChange?.({
                            ...values,
                            [scopedTitleKey(titleKey, type)]: value,
                          });
                          e.target.value = "";
                        }}
                      >
                        <option value="">Use for type…</option>
                        {TITLE_APPOINTMENT_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {APPOINTMENT_TYPE_LABELS[type]}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => {
                          const next = { ...values };
                          delete next[titleKey];
                          onChange?.(next);
                        }}
                      >
                        Discard legacy value
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="overflow-x-auto rounded-md border border-border/50">
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 text-left text-primary/50">
                <tr>
                  <th className="px-3 py-2 font-medium">Appointment type</th>
                  <th className="px-3 py-2 font-medium">System default</th>
                  {TITLE_KEYS.map((titleKey) => (
                    <th key={titleKey} className="px-3 py-2 font-medium">
                      {TITLE_LABELS[titleKey]}
                    </th>
                  ))}
                  {!readOnly && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody>
                {TITLE_APPOINTMENT_TYPES.map((type) => {
                  const anyOverride = TITLE_KEYS.some(
                    (titleKey) =>
                      effectiveTitle(values, titleKey, type).overridden,
                  );
                  return (
                    <tr
                      key={type}
                      data-appointment-type={type}
                      className="border-t border-border/40 align-middle"
                    >
                      <td className="px-3 py-2 font-medium text-primary/80">
                        {APPOINTMENT_TYPE_LABELS[type]}
                      </td>
                      <td className="px-3 py-2 text-primary/50">
                        {systemTitleDefault(type) || "—"}
                      </td>
                      {TITLE_KEYS.map((titleKey) => {
                        const key = scopedTitleKey(titleKey, type);
                        const eff = effectiveTitle(values, titleKey, type);
                        return (
                          <td key={titleKey} className="px-3 py-2">
                            {readOnly ? (
                              <span
                                className={cn(
                                  eff.overridden
                                    ? "font-medium text-primary"
                                    : "text-primary/60",
                                )}
                              >
                                {eff.value || "—"}
                                {eff.overridden && (
                                  <span className="ml-1.5 rounded bg-emerald-50 px-1 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/30">
                                    Override
                                  </span>
                                )}
                              </span>
                            ) : (
                              <Input
                                aria-label={`${TITLE_LABELS[titleKey]} for ${APPOINTMENT_TYPE_LABELS[type]}`}
                                value={values[key] ?? ""}
                                placeholder={systemTitleDefault(type)}
                                onChange={(e) => set(key, e.target.value)}
                                onBlur={() => onCommit?.()}
                                className={cn(
                                  "h-8 text-xs",
                                  eff.overridden &&
                                    "border-emerald-200 bg-emerald-50/30 dark:border-emerald-900/30 dark:bg-emerald-950/10",
                                )}
                              />
                            )}
                          </td>
                        );
                      })}
                      {!readOnly && (
                        <td className="px-3 py-2 text-right">
                          <div className="flex flex-col items-end gap-1">
                            {anyOverride && (
                              <button
                                type="button"
                                onClick={() =>
                                  onChange?.(withTypeReset(values, type))
                                }
                                className="inline-flex items-center gap-1 text-[11px] text-primary/50 hover:text-primary"
                                title={`Reset ${APPOINTMENT_TYPE_LABELS[type]} to its system default`}
                              >
                                <RotateCcw className="size-3" />
                                Reset to default
                              </button>
                            )}
                            {onApplyToAll && (
                              <button
                                type="button"
                                onClick={() => {
                                  const set = Object.fromEntries(
                                    TITLE_KEYS.flatMap((titleKey) => {
                                      const key = scopedTitleKey(
                                        titleKey,
                                        type,
                                      );
                                      const value = values[key]?.trim();
                                      return value ? [[key, value]] : [];
                                    }),
                                  );
                                  const clear = TITLE_KEYS.map((titleKey) =>
                                    scopedTitleKey(titleKey, type),
                                  ).filter((key) => !(key in set));
                                  void onApplyToAll({
                                    appointmentType: type,
                                    set,
                                    clear,
                                  });
                                }}
                                className="inline-flex items-center gap-1 text-[11px] text-primary/50 hover:text-primary"
                                title={`Apply ${APPOINTMENT_TYPE_LABELS[type]} titles to every preset in this category (blank = reset to default)`}
                              >
                                <Users className="size-3" />
                                {applyToAllLabel}
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
